import React, { useEffect, useState } from "react";
import { Backpack, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FeatureLevelControl } from "./FeatureLevelControl";
import { featureLevel, withFeatureLevel, groupFeatures } from "../../lib/permissions";
import { ROLES } from "../../lib/utils";

const PRESETS = [
    { key: "inventory_manager", label: "Gestionnaire inventaire", icon: Backpack, perms: ["inventory:manage", "printers:manage"] },
    { key: "inventory_reader", label: "Inventaire en lecture seule", icon: Eye, perms: ["inventory:view", "printers:view"] },
];

// Attribution de fonctionnalités supplémentaires à un utilisateur (en plus de celles de son rôle)
export function UserPermissionsDialog({ user, open, onClose, onSaved }) {
    const { permCatalog, refreshUsers } = useApp();
    const [extra, setExtra] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (open) setExtra(user?.permissions || []); }, [open, user]);

    if (!user || !permCatalog) return null;
    const rolePerms = permCatalog.role_permissions[user.role] || [];
    const groups = groupFeatures(permCatalog.features);
    const isAdmin = user.role === "admin";
    const roleLabel = ROLES.find((r) => r.id === user.role)?.label || user.role;

    const applyPreset = (perms) => {
        let next = extra;
        for (const p of perms) {
            const [feature, level] = p.split(":");
            next = withFeatureLevel(next, feature, level);
        }
        setExtra(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            const updated = await api.setUserPermissions(user.id, extra);
            await refreshUsers();
            toast.success("Accès mis à jour");
            onSaved?.(updated);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-permissions-dialog">
                <DialogHeader>
                    <DialogTitle>Accès aux fonctionnalités — {user.first_name} {user.last_name}</DialogTitle>
                </DialogHeader>

                <div className="flex items-center gap-2 text-xs text-gray-600 -mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 font-medium text-gray-800" data-testid="user-perm-role">{roleLabel}</span>
                    {isAdmin ? (
                        <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Dispose de toutes les fonctionnalités</span>
                    ) : (
                        <span>Le cadenas indique un droit garanti par le rôle. Vous pouvez accorder des niveaux supplémentaires.</span>
                    )}
                </div>

                {!isAdmin && (
                    <>
                        <div className="flex flex-wrap items-center gap-2 py-1">
                            <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Préréglages</span>
                            {PRESETS.map((p) => (
                                <Button key={p.key} type="button" variant="outline" size="sm" onClick={() => applyPreset(p.perms)} data-testid={`preset-${p.key}`}>
                                    <p.icon className="w-3.5 h-3.5 mr-1" />{p.label}
                                </Button>
                            ))}
                        </div>

                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                            {groups.map((g) => (
                                <div key={g.name}>
                                    <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{g.name}</div>
                                    {g.features.map((f) => (
                                        <div key={f.key} className="flex items-center justify-between gap-4 px-3 py-2.5 border-t border-gray-100" data-testid={`user-perm-row-${f.key}`}>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-900">{f.label}</div>
                                                <div className="text-[11px] text-gray-500 leading-snug">{f.description}</div>
                                            </div>
                                            <FeatureLevelControl
                                                feature={f}
                                                value={featureLevel(extra, f.key)}
                                                inherited={featureLevel(rolePerms, f.key)}
                                                onChange={(lvl) => setExtra(withFeatureLevel(extra, f.key, lvl))}
                                                testId={`user-perm-${f.key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <DialogFooter>
                    {!isAdmin && extra.length > 0 && (
                        <Button type="button" variant="ghost" onClick={() => setExtra([])} className="mr-auto text-gray-500" data-testid="user-perm-clear">Tout retirer</Button>
                    )}
                    <Button type="button" variant="outline" onClick={onClose} data-testid="user-perm-cancel">Fermer</Button>
                    {!isAdmin && <Button type="button" onClick={save} disabled={saving} data-testid="user-perm-save">{saving ? "Enregistrement..." : "Enregistrer"}</Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
